import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Sparkles, Download, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";

export default function CVEditor() {
  const { t, lang } = useI18n();
  const { refreshUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [d, setD] = useState({ identity: {}, summary: "", experiences: [], education: [], skills: [], languages: [], certifications: [] });
  const [skillInput, setSkillInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [assisting, setAssisting] = useState(false);

  useEffect(() => {
    api.get(`/work/cvs/${id}`).then(({ data }) => {
      setTitle(data.title);
      setD({ identity: {}, summary: "", experiences: [], education: [], skills: [], languages: [], certifications: [], ...(data.data || {}) });
    }).catch(() => { toast.error(t("common.error")); navigate("/work"); });
  }, [id]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/work/cvs/${id}`, { title, data: d }); toast.success(t("cv.saved")); }
    catch (e) { toast.error(t("common.error")); } finally { setSaving(false); }
  };

  const aiSummary = async () => {
    setAssisting(true);
    try {
      const { data } = await api.post("/work/cvs/assist", { section: "summary", data: { ...d.identity, experiences: d.experiences, skills: d.skills }, lang });
      setD((p) => ({ ...p, summary: data.text })); refreshUser();
    } catch (e) { toast.error(e.response?.status === 402 ? (e.response.data?.detail?.message) : t("common.error")); }
    finally { setAssisting(false); }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/work/cvs/${id}/export.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${title}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("common.error")); }
  };

  const upId = (k, v) => setD((p) => ({ ...p, identity: { ...p.identity, [k]: v } }));
  const addExp = () => setD((p) => ({ ...p, experiences: [...p.experiences, { role: "", company: "", start: "", end: "", description: "" }] }));
  const addEdu = () => setD((p) => ({ ...p, education: [...p.education, { degree: "", school: "", start: "", end: "" }] }));
  const upExp = (i, k, v) => setD((p) => { const a = [...p.experiences]; a[i] = { ...a[i], [k]: v }; return { ...p, experiences: a }; });
  const upEdu = (i, k, v) => setD((p) => { const a = [...p.education]; a[i] = { ...a[i], [k]: v }; return { ...p, education: a }; });
  const rmExp = (i) => setD((p) => ({ ...p, experiences: p.experiences.filter((_, x) => x !== i) }));
  const rmEdu = (i) => setD((p) => ({ ...p, education: p.education.filter((_, x) => x !== i) }));
  const addSkill = () => { if (skillInput.trim()) { setD((p) => ({ ...p, skills: [...p.skills, skillInput.trim()] })); setSkillInput(""); } };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" onClick={() => navigate("/work")} data-testid="cv-back"><ArrowLeft className="h-4 w-4 mr-1" />{t("common.back")}</Button>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={exportPdf} data-testid="cv-export-pdf"><Download className="h-4 w-4 mr-1.5" />{t("cv.exportPdf")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="cv-save"><Save className="h-4 w-4 mr-1.5" />{saving ? t("common.loading") : t("common.save")}</Button>
        </div>
      </div>

      <Input value={title} onChange={(e) => setTitle(e.target.value)} className="font-display text-lg font-semibold" data-testid="cv-title" />

      <section className="rounded-2xl border bg-card p-5 space-y-3">
        <h3 className="font-display font-semibold">{t("cv.identity")}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>{t("auth.fullName")}</Label><Input value={d.identity.full_name || ""} onChange={(e) => upId("full_name", e.target.value)} data-testid="cv-fullname" /></div>
          <div><Label>{t("settings.professionalTitle")}</Label><Input value={d.identity.professional_title || ""} onChange={(e) => upId("professional_title", e.target.value)} data-testid="cv-protitle" /></div>
          <div><Label>{t("common.email")}</Label><Input value={d.identity.email || ""} onChange={(e) => upId("email", e.target.value)} /></div>
          <div><Label>{t("settings.phone")}</Label><Input value={d.identity.phone || ""} onChange={(e) => upId("phone", e.target.value)} /></div>
        </div>
      </section>

      <section className="rounded-2xl border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold">{t("cv.summary")}</h3>
          <Button size="sm" variant="outline" className="rounded-full" onClick={aiSummary} disabled={assisting} data-testid="cv-ai-summary"><Sparkles className="h-4 w-4 mr-1" />{assisting ? t("common.loading") : t("cv.aiAssist")}</Button>
        </div>
        <Textarea rows={4} value={d.summary} onChange={(e) => setD({ ...d, summary: e.target.value })} data-testid="cv-summary" />
      </section>

      <section className="rounded-2xl border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between"><h3 className="font-display font-semibold">{t("cv.experiences")}</h3>
          <Button size="sm" variant="outline" className="rounded-full" onClick={addExp} data-testid="cv-add-exp"><Plus className="h-4 w-4" /></Button></div>
        {d.experiences.map((e, i) => (
          <div key={i} className="rounded-xl border p-3 space-y-2" data-testid={`cv-exp-${i}`}>
            <div className="flex gap-2">
              <Input placeholder={t("cv.role")} value={e.role} onChange={(ev) => upExp(i, "role", ev.target.value)} />
              <Input placeholder={t("work.company")} value={e.company} onChange={(ev) => upExp(i, "company", ev.target.value)} />
              <button onClick={() => rmExp(i)}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
            </div>
            <div className="flex gap-2">
              <Input placeholder={t("cv.start")} value={e.start} onChange={(ev) => upExp(i, "start", ev.target.value)} />
              <Input placeholder={t("cv.end")} value={e.end} onChange={(ev) => upExp(i, "end", ev.target.value)} />
            </div>
            <Textarea rows={2} placeholder={t("cv.description")} value={e.description} onChange={(ev) => upExp(i, "description", ev.target.value)} />
          </div>
        ))}
      </section>

      <section className="rounded-2xl border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between"><h3 className="font-display font-semibold">{t("cv.education")}</h3>
          <Button size="sm" variant="outline" className="rounded-full" onClick={addEdu} data-testid="cv-add-edu"><Plus className="h-4 w-4" /></Button></div>
        {d.education.map((e, i) => (
          <div key={i} className="rounded-xl border p-3 flex gap-2" data-testid={`cv-edu-${i}`}>
            <Input placeholder={t("cv.degree")} value={e.degree} onChange={(ev) => upEdu(i, "degree", ev.target.value)} />
            <Input placeholder={t("cv.school")} value={e.school} onChange={(ev) => upEdu(i, "school", ev.target.value)} />
            <button onClick={() => rmEdu(i)}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border bg-card p-5 space-y-3">
        <h3 className="font-display font-semibold">{t("cv.skills")}</h3>
        <div className="flex flex-wrap gap-2">
          {d.skills.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm" data-testid={`cv-skill-${i}`}>
              {s}<button onClick={() => setD((p) => ({ ...p, skills: p.skills.filter((_, x) => x !== i) }))}>×</button>
            </span>
          ))}
        </div>
        <Input value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())} placeholder={t("cv.skillPlaceholder")} data-testid="cv-skill-input" />
      </section>
    </div>
  );
}
